import { FormProvider, useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications';
import { useQueryClient } from '@mint-lib/query-client';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@mint-lib/tw-components';
import { ApprovalFlows } from '@mint-sdk/api-approval-flows';
import { getRouteApi } from '@mint/routing';

import useCreateFlowMutation from '../api/mutations/useCreateFlowMutation.js';
import CreateApprovalHeader from '../components/CreateApprovalHeader.jsx';
import { LeavePageModal } from '../components/modals/LeavePageModal';
import {
  FlowTypesEnum,
  PENDING_REQUEST_NOTIFICATION,
} from '../constants/singleFlow.js';
import { getTabsList } from '../constants/tabs.js';
import useFlowCreationOperation from '../hooks/useFlowCreationOperation.js';
import {
  getFlowFormSchema,
  type FlowFormInput,
  type FlowFormOutput,
} from '../types/singleFlow.js';
import { mapFlowFormToCreateFlowItemRequest } from '../utils/transformers.js';

const routeApi = getRouteApi('/_secured/company-profile/flows/new-flow/');

const NewFlowPage = () => {
  const { t } = useTranslation('@myn/approval_v2');
  const navigate = routeApi.useNavigate();

  const queryClient = useQueryClient();
  const { mutateAsync: createFlow, isPending } = useCreateFlowMutation();
  const flowCreationOperation = useFlowCreationOperation();

  const formMethods = useForm<FlowFormInput, unknown, FlowFormOutput>({
    mode: 'onChange',
    resolver: zodResolver(getFlowFormSchema(t)),
    defaultValues: {
      name: '',
      type: FlowTypesEnum.enum.flight,
      requesterType: null,
      requesterValue: [],
      approvers: [
        {
          entityId: null,
          entityLabel: null,
          type: null,
          operation: null,
        },
      ],
      pendingRequestNotification: {
        enabled: true,
        frequencyDays: PENDING_REQUEST_NOTIFICATION.DEFAULT_FREQUENCY_DAYS,
      },
      approvedRequestNotification: {
        enabled: false,
        recipientEmails: '',
      },
      dateFrom: new Date(),
      dateTo: null,
    },
  });

  const {
    handleSubmit,
    formState: { isDirty },
    getValues,
    resetDefaultValues,
  } = formMethods;

  const tabsList = getTabsList(t);

  const shouldBlockFn = () => {
    return isDirty;
  };

  const handleFormSubmit = handleSubmit(async (data) => {
    flowCreationOperation.restartIfEnded();
    try {
      await createFlow({
        data: mapFlowFormToCreateFlowItemRequest(data),
      });
      flowCreationOperation.succeed({
        flowType: data.type,
        approverGroupCount: data.approvers.length,
      });
      ApprovalFlows.listFlows.invalidateAll(queryClient, {});

      enqueueSnackbar({
        variant: 'success',
        title: t('Flow created.'),
      });
      resetDefaultValues(getValues(), {
        keepDirty: false,
        keepIsValid: false,
      });
      setTimeout(() => {
        navigate({
          to: '/company-profile/flows',
          replace: true,
        });
      });
    } catch {
      flowCreationOperation.fail();
      enqueueSnackbar({
        variant: 'error',
        title: t('Flow was not created.'),
      });
    }
  });

  return (
    <FormProvider {...formMethods}>
      <form className="relative" onSubmit={handleFormSubmit}>
        <div className="grid gap-4">
          <CreateApprovalHeader
            createdAt={new Date()}
            actionDisabled={!formMethods.formState.isValid}
            isPending={isPending}
          />
          <div>
            <Tabs defaultValue={tabsList[0].id} orientation="horizontal">
              <TabsList className="w-full">
                <TabsTrigger
                  value="mainInfo"
                  data-dd-action-name="New flow: open main information tab"
                >
                  {t('Main information')}
                </TabsTrigger>
                <TabsTrigger
                  value="flowCreation"
                  data-dd-action-name="New flow: open flow creation tab"
                >
                  {t('Flow creation')}
                </TabsTrigger>
                <TabsTrigger
                  value="notifications"
                  data-dd-action-name="New flow: open notifications tab"
                >
                  {t('Notifications')}
                </TabsTrigger>
              </TabsList>
              <div className="mt-6">
                {tabsList.map(({ id, component: Component }) => {
                  return (
                    <TabsContent key={id} value={id}>
                      {<Component isFlowInUse={false} />}
                    </TabsContent>
                  );
                })}
              </div>
            </Tabs>
          </div>
        </div>
      </form>
      <LeavePageModal shouldBlockFn={shouldBlockFn} />
    </FormProvider>
  );
};

export default NewFlowPage;
